/**
 * Google Geocoding API Provider
 *
 * Implements the GeocodingProvider interface using Google's Geocoding API.
 *
 * API Documentation: https://developers.google.com/maps/documentation/geocoding/requests-geocoding
 *
 * Features:
 * - Forward geocoding (address to coordinates)
 * - Reverse geocoding (coordinates to address)
 * - Region and bounds biasing
 * - Full address component parsing
 * - Plus Code extraction/generation
 */

import { OpenLocationCode } from 'open-location-code'

// Instantiate the Plus Code generator
const plusCodeGenerator = new OpenLocationCode()
import type {
  GeocodingProvider,
  GeocodeRequest,
  GeocodeResult,
  GeocodeComponents,
  ReverseGeocodeResult,
  LocationType,
} from '../../types/geocoding'
import type { LatLng } from '../../types/common'
import { GeocodingError } from '../../types/common'
import type { GoogleClient } from './client'

const GEOCODING_API_URL = 'https://maps.googleapis.com/maps/api/geocode/json'
const PLACE_DETAILS_API_URL = 'https://places.googleapis.com/v1/places'
const PLACE_DETAILS_FIELD_MASK = [
  'id',
  'formattedAddress',
  'location',
  'addressComponents',
  'plusCode',
].join(',')

/** Google Geocoding API response */
interface GoogleGeocodingResponse {
  status: GoogleGeocodingStatus
  results: GoogleGeocodingResult[]
  error_message?: string
}

type GoogleGeocodingStatus =
  | 'OK'
  | 'ZERO_RESULTS'
  | 'OVER_DAILY_LIMIT'
  | 'OVER_QUERY_LIMIT'
  | 'REQUEST_DENIED'
  | 'INVALID_REQUEST'
  | 'UNKNOWN_ERROR'

interface GoogleGeocodingResult {
  formatted_address: string
  geometry: {
    location: { lat: number; lng: number }
    location_type: string
    viewport?: {
      northeast: { lat: number; lng: number }
      southwest: { lat: number; lng: number }
    }
    bounds?: {
      northeast: { lat: number; lng: number }
      southwest: { lat: number; lng: number }
    }
  }
  place_id: string
  plus_code?: {
    global_code: string
    compound_code?: string
  }
  address_components: GoogleAddressComponent[]
  types: string[]
  partial_match?: boolean
}

interface GoogleAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

/** Map Google location_type to our canonical LocationType */
const LOCATION_TYPE_MAP: Record<string, LocationType> = {
  ROOFTOP: 'ROOFTOP',
  RANGE_INTERPOLATED: 'RANGE_INTERPOLATED',
  GEOMETRIC_CENTER: 'GEOMETRIC_CENTER',
  APPROXIMATE: 'APPROXIMATE',
}

/** Map Google address component types to our canonical field names */
const COMPONENT_TYPE_MAP: Record<string, keyof GeocodeComponents> = {
  street_number: 'streetNumber',
  route: 'route',
  locality: 'city',
  sublocality: 'city', // fallback for city
  sublocality_level_1: 'city', // another fallback
  administrative_area_level_2: 'county',
  administrative_area_level_1: 'state',
  postal_code: 'postalCode',
  country: 'country',
  neighborhood: 'neighborhood',
}

/** Google Places API (New) Place Details response */
interface GooglePlaceDetailsResponse {
  id: string
  formattedAddress?: string
  location?: { latitude: number; longitude: number }
  addressComponents?: GooglePlaceAddressComponent[]
  plusCode?: { globalCode?: string; compoundCode?: string }
  error?: { code: number; message: string; status: string }
}

interface GooglePlaceAddressComponent {
  longText: string
  shortText: string
  types: string[]
  languageCode?: string
}

export class GoogleGeocodingProvider implements GeocodingProvider {
  readonly name = 'google_geocoding'
  private client: GoogleClient

  constructor(client: GoogleClient) {
    this.client = client
  }

  /**
   * Resolve a Place ID to full geocode data using Places API (New).
   *
   * Unlike the legacy Geocoding API, the Places API (New) works with
   * browser API keys that have HTTP referer restrictions, making this
   * suitable for frontend usage.
   */
  async fetchPlaceDetails(placeId: string): Promise<GeocodeResult> {
    const url = `${PLACE_DETAILS_API_URL}/${placeId}`

    const response = await this.client.get<GooglePlaceDetailsResponse>(
      url,
      {
        headers: {
          'X-Goog-FieldMask': PLACE_DETAILS_FIELD_MASK,
        },
      }
    )

    if (response.error) {
      throw new GeocodingError(
        response.error.message,
        response.error.status,
        placeId
      )
    }

    if (!response.location) {
      throw new GeocodingError(
        'Place details response missing location',
        'MISSING_LOCATION',
        placeId
      )
    }

    return this.mapPlaceDetailsToCanonical(response, placeId)
  }

  async geocode(request: GeocodeRequest): Promise<GeocodeResult> {
    const queryParams = this.buildQueryParams(request)

    const response = await this.client.get<GoogleGeocodingResponse>(
      GEOCODING_API_URL,
      { queryParams }
    )

    this.validateResponse(response, request.address)

    return this.mapToCanonical(response.results[0])
  }

  async reverseGeocode(location: LatLng): Promise<ReverseGeocodeResult> {
    const queryParams: Record<string, string> = {
      latlng: `${location.latitude},${location.longitude}`,
    }

    const response = await this.client.get<GoogleGeocodingResponse>(
      GEOCODING_API_URL,
      { queryParams }
    )

    this.validateResponse(
      response,
      `${location.latitude},${location.longitude}`
    )

    const result = response.results[0]
    const components = this.extractComponents(result.address_components)

    return {
      formattedAddress: result.formatted_address,
      placeId: result.place_id,
      components,
      source: this.name,
      geocodedAt: new Date().toISOString(),
    }
  }

  private buildQueryParams(request: GeocodeRequest): Record<string, string> {
    const params: Record<string, string> = {
      address: request.address,
    }

    if (request.regionBias) {
      params.region = request.regionBias
    }

    if (request.language) {
      params.language = request.language
    }

    if (request.bounds) {
      const { south, west, north, east } = request.bounds
      params.bounds = `${south},${west}|${north},${east}`
    }

    return params
  }

  private validateResponse(
    response: GoogleGeocodingResponse,
    address: string
  ): void {
    const { status, error_message } = response

    switch (status) {
      case 'OK':
        if (response.results.length === 0) {
          throw new GeocodingError(
            'No results returned',
            'ZERO_RESULTS',
            address
          )
        }
        return

      case 'ZERO_RESULTS':
        throw new GeocodingError(
          'No results found for address',
          'ZERO_RESULTS',
          address
        )

      case 'OVER_DAILY_LIMIT':
      case 'OVER_QUERY_LIMIT':
        throw new GeocodingError(
          error_message ?? 'API quota exceeded',
          status,
          address,
          { retryable: true }
        )

      case 'REQUEST_DENIED':
        throw new GeocodingError(
          error_message ?? 'Request denied - check API key',
          'REQUEST_DENIED',
          address
        )

      case 'INVALID_REQUEST':
        throw new GeocodingError(
          error_message ?? 'Invalid request - check address format',
          'INVALID_REQUEST',
          address
        )

      default:
        throw new GeocodingError(
          error_message ?? `Geocoding failed: ${status}`,
          status,
          address
        )
    }
  }

  private mapToCanonical(result: GoogleGeocodingResult): GeocodeResult {
    const components = this.extractComponents(result.address_components)
    const { lat, lng } = result.geometry.location
    const locationType = this.mapLocationType(result.geometry.location_type)

    // Build street address from components
    const streetAddress = this.buildStreetAddress(components, result)

    // Get or generate Plus Code
    const plusCode = this.extractOrGeneratePlusCode(result, lat, lng)

    return {
      streetAddress,
      formattedAddress: result.formatted_address,
      latitude: lat,
      longitude: lng,
      placeId: result.place_id,
      plusCode,
      locationType,
      components,
      source: this.name,
      geocodedAt: new Date().toISOString(),
    }
  }

  private extractComponents(
    addressComponents: GoogleAddressComponent[]
  ): GeocodeComponents {
    const components: GeocodeComponents = {}

    for (const component of addressComponents) {
      for (const type of component.types) {
        const mappedField = COMPONENT_TYPE_MAP[type]
        if (mappedField && !components[mappedField]) {
          // Use long_name for most fields
          components[mappedField] = component.long_name

          // Also extract short names for state and country codes
          if (type === 'administrative_area_level_1') {
            components.stateCode = component.short_name
          }
          if (type === 'country') {
            components.countryCode = component.short_name
          }
        }
      }
    }

    return components
  }

  private buildStreetAddress(
    components: GeocodeComponents,
    result: GoogleGeocodingResult
  ): string {
    // Try to build from components
    if (components.streetNumber && components.route) {
      return `${components.streetNumber} ${components.route}`
    }

    // Fall back to extracting from formatted address
    // (take the part before the first comma)
    const firstPart = result.formatted_address.split(',')[0]
    return firstPart?.trim() ?? result.formatted_address
  }

  private mapLocationType(googleType: string): LocationType {
    return LOCATION_TYPE_MAP[googleType] ?? 'APPROXIMATE'
  }

  private extractOrGeneratePlusCode(
    result: GoogleGeocodingResult,
    lat: number,
    lng: number
  ): string {
    // Prefer Google's Plus Code if available
    if (result.plus_code?.global_code) {
      return result.plus_code.global_code
    }

    // Generate using open-location-code library
    return plusCodeGenerator.encode(lat, lng)
  }

  private mapPlaceDetailsToCanonical(
    response: GooglePlaceDetailsResponse,
    placeId: string
  ): GeocodeResult {
    const lat = response.location!.latitude
    const lng = response.location!.longitude
    const components = this.extractPlaceDetailsComponents(response.addressComponents ?? [])

    const streetAddress = this.buildStreetAddressFromComponents(components, response.formattedAddress ?? '')

    const plusCode = response.plusCode?.globalCode ?? plusCodeGenerator.encode(lat, lng)

    return {
      streetAddress,
      formattedAddress: response.formattedAddress ?? '',
      latitude: lat,
      longitude: lng,
      placeId,
      plusCode,
      locationType: 'ROOFTOP',
      components,
      source: 'google_place_details',
      geocodedAt: new Date().toISOString(),
    }
  }

  private extractPlaceDetailsComponents(
    addressComponents: GooglePlaceAddressComponent[]
  ): GeocodeComponents {
    const components: GeocodeComponents = {}

    for (const component of addressComponents) {
      for (const type of component.types) {
        const mappedField = COMPONENT_TYPE_MAP[type]
        if (mappedField && !components[mappedField]) {
          components[mappedField] = component.longText

          if (type === 'administrative_area_level_1') {
            components.stateCode = component.shortText
          }
          if (type === 'country') {
            components.countryCode = component.shortText
          }
        }
      }
    }

    return components
  }

  private buildStreetAddressFromComponents(
    components: GeocodeComponents,
    formattedAddress: string
  ): string {
    if (components.streetNumber && components.route) {
      return `${components.streetNumber} ${components.route}`
    }

    const firstPart = formattedAddress.split(',')[0]
    return firstPart?.trim() ?? formattedAddress
  }
}
